import React, { useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';

interface VersionHistoryWidgetProps {
    mainProductVersion: {
        currentVersion: number;
        totalVersions: number;
        jumpToVersion: (version: number) => void;
        resetToLatest: () => void;
        isLoading: boolean;
    };
    itemVersion: {
        currentVersion: number;
        totalVersions: number;
        jumpToVersion: (version: number) => void;
        resetToLatest: () => void;
        isLoading: boolean;
    };
    isItem: boolean;
    mainProductName?: string;
    itemName?: string;
    showKeyboardHint: boolean;
    onCycleVersions?: (cycleFn: () => void) => void;
}

const VersionHistoryWidget: React.FC<VersionHistoryWidgetProps> = ({
    mainProductVersion,
    itemVersion,
    isItem,
    showKeyboardHint,
    onCycleVersions,
}) => {
    // itemVersion holds the primary product data even on a main product page,
    // so both versions are checked regardless of product type.
    const isMainHistorical = mainProductVersion.totalVersions > 1 && mainProductVersion.currentVersion < mainProductVersion.totalVersions;
    const isSubHistorical = itemVersion.totalVersions > 1 && itemVersion.currentVersion < itemVersion.totalVersions;
    const isHistorical = isMainHistorical || isSubHistorical;

    const handleCycleVersions = useCallback(() => {
        // Reset (loop back to latest) once fully stepped back: sub-products need
        // both versions at min; main products only itemVersion.
        const mainAtMin = mainProductVersion.currentVersion <= 1;
        const subAtMin = itemVersion.currentVersion <= 1;

        const shouldReset = isItem ? (mainAtMin && subAtMin) : subAtMin;

        if (shouldReset) {
            // Always reset itemVersion as it holds the current product state (whether main or sub)
            itemVersion.resetToLatest();
            // Only reset mainProductVersion if we are actually in a sub-product context
            if (isItem) mainProductVersion.resetToLatest();
            return;
        }

        let nextMain = mainProductVersion.currentVersion - 1;
        let nextSub = itemVersion.currentVersion - 1;

        if (nextMain < 1) nextMain = 1;
        if (nextSub < 1) nextSub = 1;

        if (isItem) mainProductVersion.jumpToVersion(nextMain);
        itemVersion.jumpToVersion(nextSub);
    }, [isItem, mainProductVersion, itemVersion]);

    // Expose cycle function to parent
    useEffect(() => {
        if (onCycleVersions) {
            onCycleVersions(handleCycleVersions);
        }
    }, [onCycleVersions, handleCycleVersions]);

    // Keys live in the page's shortcut bar (#467): H steps back through the
    // versions (through onCycleVersions above), Esc returns to the latest.

    return (
        <AnimatePresence>
            {isHistorical && showKeyboardHint && (
                <motion.div
                    className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-50"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.5, duration: 0.4 }}
                >
                    <div className="bg-black/40 backdrop-blur-md border border-white/10 shadow-lg rounded-xl px-4 py-3 flex items-center gap-4">
                        <div className="flex items-center gap-2 text-white/60">
                            <kbd className="px-1.5 py-0.5 bg-white/10 rounded font-mono text-xs text-white/80">
                                H
                            </kbd>
                            <span className="text-xs font-medium text-[#f2b056] flex items-center gap-1.5">
                                <AlertTriangle size={12} />
                                Earlier version
                            </span>
                        </div>

                        <div className="w-px h-4 bg-white/20" />

                        <div className="flex items-center gap-3 text-xs">
                            {/* Main Product Version */}
                            {(isItem || (!isItem && itemVersion.totalVersions > 1)) && (
                                <div className="flex items-center gap-1.5">
                                    <span className="text-white/40">
                                        {isItem ? 'Main:' : 'Version:'}
                                    </span>
                                    <span className="font-mono text-[#f2b056] bg-[#f2b056]/10 px-1.5 py-0.5 rounded">
                                        v{isItem ? mainProductVersion.currentVersion : itemVersion.currentVersion}
                                        <span className="text-white/30">/{isItem ? mainProductVersion.totalVersions : itemVersion.totalVersions}</span>
                                    </span>
                                </div>
                            )}

                            {/* Sub Product Version */}
                            {isItem && itemVersion.totalVersions > 1 && (
                                <div className="flex items-center gap-1.5">
                                    <span className="text-white/40">Sub:</span>
                                    <span className="font-mono text-william-200 bg-white/10 px-1.5 py-0.5 rounded">
                                        v{itemVersion.currentVersion}
                                        <span className="text-white/30">/{itemVersion.totalVersions}</span>
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default VersionHistoryWidget;
